import { notifications } from "@mantine/notifications";
import { Dispatch, useCallback } from "react";
import { HeldEmailBulkResult } from "../../services/admin/systemEmails.service";
import {
  HeldEmailQueueAction,
  HeldEmailQueueState,
} from "./useHeldEmailQueueState";
import { useCancelHeldEmails, useReleaseHeldEmails } from "./useSystemEmails";

type Mutation = ReturnType<typeof useReleaseHeldEmails>;

interface PerformOptions {
  ids: string[];
  label: string;
  mutation: Mutation;
  dispatch: Dispatch<HeldEmailQueueAction>;
}

function showResult(label: string, result: HeldEmailBulkResult): void {
  notifications.show({
    color: result.failed ? "yellow" : "green",
    title: `${label} complete`,
    message: `${result.success} succeeded${
      result.failed ? `; ${result.failed} could not be processed` : ""
    }.`,
  });
}

async function perform(options: PerformOptions): Promise<void> {
  const { ids, label, mutation, dispatch } = options;
  try {
    showResult(label, await mutation.mutateAsync(ids));
    dispatch({ type: "selection", value: new Set() });
    dispatch({ type: "action", value: null });
  } catch (error) {
    notifications.show({
      color: "red",
      title: `${label} failed`,
      message: (error as Error).message,
    });
  }
}

export function useReleaseHeldEmailAction(
  state: HeldEmailQueueState,
  dispatch: Dispatch<HeldEmailQueueAction>
) {
  const mutation = useReleaseHeldEmails();
  const confirm = useCallback(
    () =>
      state.pendingAction?.type === "release"
        ? perform({
            ids: state.pendingAction.ids,
            label: "Release",
            mutation,
            dispatch,
          })
        : undefined,
    [dispatch, mutation, state.pendingAction]
  );
  return { mutation, confirm };
}

export function useCancelHeldEmailAction(
  state: HeldEmailQueueState,
  dispatch: Dispatch<HeldEmailQueueAction>
) {
  const mutation = useCancelHeldEmails();
  const confirm = useCallback(
    () =>
      state.pendingAction?.type === "cancel"
        ? perform({
            ids: state.pendingAction.ids,
            label: "Cancellation",
            mutation,
            dispatch,
          })
        : undefined,
    [dispatch, mutation, state.pendingAction]
  );
  return { mutation, confirm };
}
